import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Bot, Play, Pause, Settings2, Loader2, TrendingUp, MessageSquare,
  LayoutDashboard, Megaphone, History, Clock, PlugZap, Briefcase
} from 'lucide-react';
import BusinessTab from '../../components/traffic-manager/BusinessTab';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import ManagerForm, { FormState, formFrom, toApi, defaultCurrency } from '../../components/traffic-manager/ManagerForm';
import SetupWizard from '../../components/traffic-manager/SetupWizard';
import OverviewTab from '../../components/traffic-manager/OverviewTab';
import ChatTab from '../../components/traffic-manager/ChatTab';
import { ChangesTab, RunsTab } from '../../components/traffic-manager/HistoryTabs';
import { LearningTab } from '../../components/traffic-manager/LearningTabs';
import CampaignsTab from '../../components/traffic-manager/CampaignsTab';
import ExportMenu from '../../components/traffic-manager/ExportMenu';
import HealthTab, { AlertsBanner } from '../../components/traffic-manager/HealthTab';
import { useFmt, useLabels } from '../../components/traffic-manager/tmShared';
import trafficManagerService, { TrafficManager, TrafficRun, Guardrails, EligibleCampaign, Evolution, AdsHealth } from '../../services/trafficManagerService';
import AiCostBadge from '../../components/ai/AiCostBadge';

type Tab = 'overview' | 'chat' | 'campaigns' | 'business' | 'changes' | 'runs' | 'learning' | 'health';
const TABS: Tab[] = ['overview', 'chat', 'campaigns', 'business', 'changes', 'runs', 'learning', 'health'];

const TrafficManagerPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const f = useFmt();
  const L = useLabels();
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'overview';
  const setTab = (x: Tab) => { const p = new URLSearchParams(params); p.set('tab', x); setParams(p, { replace: true }); };

  const [loading, setLoading] = useState(true);
  const [manager, setManager] = useState<TrafficManager | null>(null);
  const [defaults, setDefaults] = useState<Guardrails | null>(null);
  const [runs, setRuns] = useState<TrafficRun[]>([]);
  const [evolution, setEvolution] = useState<Evolution | null>(null);
  const [eligible, setEligible] = useState<EligibleCampaign[]>([]);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busyRun, setBusyRun] = useState<string | null>(null);
  const [campaignFilter, setCampaignFilter] = useState('');
  const [health, setHealth] = useState<AdsHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const loadHealth = useCallback(async (fresh = false) => {
    setHealthLoading(true);
    try { setHealth(await trafficManagerService.health(fresh)); } catch { /* a tela segue sem alertas */ } finally { setHealthLoading(false); }
  }, []);
  useEffect(() => { loadHealth(false); }, [loadHealth]);
  const poll = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data, defaults: d } = await trafficManagerService.list();
      setDefaults(d);
      if (!data.length) setEligible(await trafficManagerService.eligibleCampaigns());
      if (data.length) {
        const id = data[0].id;
        // Com o gestor: cada campanha já vem dizendo se está ou não sob gestão.
        const [full, hist, el] = await Promise.all([trafficManagerService.get(id), trafficManagerService.runs(id, 25, 'routine'), trafficManagerService.eligibleCampaigns(id)]);
        setEligible(el);
        setManager(full);
        setRuns(hist);
        trafficManagerService.evolution(id, 60).then(setEvolution).catch(() => setEvolution(null));
      } else {
        setManager(null); setRuns([]); setEvolution(null);
      }
    } catch (e: any) {
      if (!quiet) toast.error(e?.response?.data?.message || t('trafficManager.loadError', 'Não foi possível carregar o gestor de tráfego'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Rodada em andamento: acompanha a cada 5s até terminar.
  const running = runs.some((r) => r.status === 'running');
  useEffect(() => {
    if (poll.current) clearTimeout(poll.current);
    if (running) poll.current = setTimeout(() => load(true), 5000);
    return () => { if (poll.current) clearTimeout(poll.current); };
  }, [running, runs, load]);

  const save = async (form: FormState) => {
    setSaving(true);
    try {
      if (manager) {
        await trafficManagerService.update(manager.id, toApi(form, i18n.language));
        toast.success(t('trafficManager.saved', 'Configuração salva'));
        setEditing(false);
      } else {
        await trafficManagerService.create(toApi(form, i18n.language));
        toast.success(t('trafficManager.created', 'Gestor ativado. A primeira rodada acontece no próximo horário — ou rode agora.'));
      }
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar'));
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!manager) return;
    setStarting(true);
    try {
      const { already } = await trafficManagerService.runNow(manager.id);
      toast.success(already ? t('trafficManager.alreadyRunning', 'Já existe uma rodada em andamento') : t('trafficManager.runStarted', 'Rodada iniciada. Acompanhe na aba Rodadas.'));
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.runError', 'Não foi possível iniciar a rodada'));
    } finally { setStarting(false); }
  };

  const toggleStatus = async () => {
    if (!manager) return;
    try {
      await trafficManagerService.update(manager.id, { status: manager.status === 'active' ? 'paused' : 'active' });
      toast.success(manager.status === 'active' ? t('trafficManager.paused', 'Gestor pausado. Nenhuma rodada automática até você reativar.') : t('trafficManager.resumed', 'Gestor reativado'));
      await load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar'));
    }
  };

  const decide = async (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => {
    setBusyRun(runId);
    try {
      const acts = d === 'approve' ? await trafficManagerService.approve(runId, actionId) : await trafficManagerService.reject(runId, actionId);
      setRuns((rs) => rs.map((r) => (r.id === runId ? { ...r, actions: acts } : r)));
      if (d === 'approve') {
        const target = actionId ? acts.filter((a) => a.id === actionId) : acts.filter((a) => a.decided_at && Date.now() - new Date(a.decided_at).getTime() < 60000);
        const bad = target.filter((a) => a.status === 'failed' || a.status === 'blocked').length;
        if (bad) toast.error(t('trafficManager.approvePartial', '{{n}} ação(ões) não puderam ser aplicadas; veja o motivo na lista.', { n: bad }));
        else toast.success(t('trafficManager.approved', 'Aplicado nas contas'));
      }
      load(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('trafficManager.decideError', 'Não foi possível decidir a proposta'));
    } finally { setBusyRun(null); }
  };

  const initialForm = useMemo(() => (defaults ? formFrom(manager, defaults, t('trafficManager.defaultName', 'Gestor de tráfego') as string, defaultCurrency(eligible)) : null), [manager, defaults, t, eligible]);
  const pendingTotal = runs.reduce((acc, r) => acc + (r.actions || []).filter((a) => a.status === 'proposed').length, 0);

  if (loading || !initialForm) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }

  // ------------------------- Primeiro acesso -------------------------
  if (!manager) {
    return <SetupWizard initial={initialForm} eligible={eligible} saving={saving} onSave={save} />;
  }

  // ------------------------- Gestor ativo -------------------------
  const tabMeta: Record<Tab, { label: string; icon: React.ElementType; badge?: number }> = {
    overview: { label: t('trafficManager.tabs.overview', 'Visão geral'), icon: LayoutDashboard, badge: pendingTotal || undefined },
    chat: { label: t('trafficManager.tabs.chat', 'Conversa'), icon: MessageSquare },
    campaigns: { label: t('trafficManager.tabs.campaigns', 'Campanhas'), icon: Megaphone },
    business: { label: t('trafficManager.tabs.business', 'Negócio e materiais'), icon: Briefcase },
    changes: { label: t('trafficManager.tabs.changes', 'Mudanças'), icon: History },
    runs: { label: t('trafficManager.tabs.runs', 'Rodadas'), icon: Clock },
    learning: { label: t('trafficManager.tabs.learning', 'Aprendizado'), icon: TrendingUp },
    health: { label: t('trafficManager.tabs.health', 'Conexões'), icon: PlugZap, badge: health ? health.alerts.filter((a) => a.severity === 'critical').length || undefined : undefined }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary-600 shrink-0" />
            <span className="truncate">{manager.name}</span>
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${manager.status === 'active' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${manager.status === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
              {manager.status === 'active' ? t('trafficManager.active', 'Ativo') : t('trafficManager.pausedLabel', 'Pausado')}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              {manager.mode === 'autopilot' ? t('trafficManager.form.autopilotTitle', 'Piloto automático') : t('trafficManager.form.approvalTitle', 'Pedir minha aprovação')}
            </span>
            <span className="text-gray-500">{L.goal(manager.goal)} · {f.money(manager.monthly_budget, manager.currency)}/{t('trafficManager.month', 'mês')}</span>
            {running && <span className="inline-flex items-center gap-1 text-primary-700 dark:text-primary-300"><Loader2 className="w-3 h-3 animate-spin" />{t('trafficManager.analyzing', 'Analisando as campanhas…')}</span>}
            {!running && manager.status === 'active' && manager.next_run_at && <span className="text-gray-500">· {t('trafficManager.nextRun', 'próxima rodada {{d}}', { d: f.dateTime(manager.next_run_at) })}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ExportMenu managerId={manager.id} />
          <Button variant="secondary" size="sm" className="h-10" onClick={() => setEditing(true)} leftIcon={<Settings2 className="w-4 h-4" />}>
            <span className="hidden sm:inline">{t('trafficManager.configure', 'Configurar')}</span>
          </Button>
          <Button variant="secondary" size="sm" className="h-10" onClick={toggleStatus} leftIcon={manager.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}>
            <span className="hidden sm:inline">{manager.status === 'active' ? t('trafficManager.pause', 'Pausar') : t('trafficManager.resume', 'Reativar')}</span>
          </Button>
          <Button size="sm" className="h-10" onClick={runNow} disabled={starting || running} leftIcon={starting || running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}>
            {running ? t('trafficManager.running', 'Rodando…') : t('trafficManager.runNow', 'Rodar agora')}
          </Button>
        </div>
      </div>
      {/* Cada rodada (automática ou "Rodar agora") usa IA para analisar e decidir */}
      <AiCostBadge feature="traffic-manager" unitLabel={t('aiCredits.perRound', 'por rodada')} />

      <nav className="tm-tabs flex gap-1 overflow-x-auto overflow-y-hidden -mx-1 px-1 shadow-[inset_0_-1px_0_#e5e7eb] dark:shadow-[inset_0_-1px_0_#374151]" role="tablist">
        {TABS.map((k) => {
          const m = tabMeta[k];
          const Icon = m.icon;
          const on = tab === k;
          return (
            <button key={k} role="tab" aria-selected={on} onClick={() => setTab(k)}
              className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 shrink-0 transition-colors ${on ? 'border-primary-600 text-primary-700 dark:text-primary-300 font-medium' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>
              <Icon className="w-4 h-4" />{m.label}
              {m.badge ? <span className="ml-0.5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold">{m.badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === 'overview' && <AlertsBanner health={health} onOpen={() => setTab('health')} />}
      {tab === 'health' && <HealthTab health={health} loading={healthLoading} onRefresh={() => loadHealth(true)} onHealth={(h) => { setHealth(h); load(true); }} />}
      {tab === 'overview' && <OverviewTab manager={manager} evolution={evolution} runs={runs} busyRun={busyRun} onDecide={decide} onOpenRuns={() => setTab('runs')} />}
      {tab === 'chat' && <ChatTab manager={manager} onDecide={decide} onManagerChange={(m) => setManager((old) => (old ? { ...old, ...m, snapshot: old.snapshot } : m))} />}
      {tab === 'campaigns' && <CampaignsTab manager={manager} evolution={evolution} eligible={eligible} onSeeChanges={(id) => { setCampaignFilter(id); setTab('changes'); }} onScopeSaved={() => load(true)} />}
      {tab === 'business' && <BusinessTab manager={manager} onManagerChange={(m) => setManager((old) => (old ? { ...old, ...m, snapshot: old.snapshot } : m))} />}
      {tab === 'changes' && <ChangesTab manager={manager} campaignFilter={campaignFilter} onCampaignFilter={setCampaignFilter} />}
      {tab === 'runs' && <RunsTab manager={manager} runs={runs} onDecide={decide} />}
      {tab === 'learning' && <LearningTab manager={manager} evolution={evolution} onManagerChange={(m) => setManager((old) => (old ? { ...old, ...m, snapshot: old.snapshot } : m))} />}

      <Modal isOpen={editing} onClose={() => setEditing(false)} title={t('trafficManager.configureTitle', 'Configurar gestor de tráfego') as string} size="3xl" maxHeight>
        <ManagerForm key={manager.id + String(editing)} initial={initialForm} eligible={eligible} saving={saving} onSave={save} onCancel={() => setEditing(false)} submitLabel={t('common.save', 'Salvar') as string} />
        <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            className="text-sm text-red-600 hover:underline"
            onClick={async () => {
              if (!window.confirm(t('trafficManager.deleteConfirm', 'Excluir o gestor e todo o histórico de rodadas? As campanhas não são alteradas.') as string)) return;
              try {
                await trafficManagerService.remove(manager.id);
                setEditing(false);
                toast.success(t('trafficManager.deleted', 'Gestor excluído'));
                load();
              } catch (e: any) {
                toast.error(e?.response?.data?.message || t('trafficManager.saveError', 'Não foi possível salvar'));
              }
            }}
          >
            {t('trafficManager.delete', 'Excluir gestor')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TrafficManagerPage;
